// src/utils/store.ts
// M5-STORE1 (15.09.2026): первый заход хранилища — тонкий слой над нативным
// IndexedDB, без сторонних библиотек. В этом заходе им пользуется только
// snapshot.ts; content/badges/progress — следующим заходом, когда схема
// проверена на живом снапшоте (см. задание трека).
// M5-STORE2 (16.09.2026): второй заход — content.ts/progress.ts/badges.ts
// подключены к тому же слою. Их записи зависят от courseId (и дня для
// контента), поэтому формат составного ключа вынесен сюда одним местом
// (compositeKey ниже) — правка формата не должна разъезжаться по трём
// модулям-потребителям порознь.

const DB_NAME = "mira-store";
// Смена номера — миграция (onupgradeneeded) или осознанный сброс, не молчаливое
// чтение старой формы записи.
const DB_VERSION = 1;
const RECORDS_STORE = "records";

// Грейс офлайна — 3 дня. Связано с окном предзагрузки контента (content-access,
// долг D4: окно предзагрузки должно совпадать с грейсом офлайна, иначе они
// разойдутся и офлайн-режим либо не покроет предзагруженное, либо покажет
// протухшее дальше, чем контент реально предзагружен). Пока предзагрузка не
// реализована, это единственное место, где грейс закреплён числом — при
// реализации D4 обе величины берутся отсюда, не подбираются заново.
export const STALE_AFTER_MS = 3 * 24 * 60 * 60 * 1000;

type StoreRecord<T> = {
  data: T;
  cachedAt: number;
  userId: number;
};

export type StoreGetResult<T> =
  | { status: "fresh"; data: T; cachedAt: number; userId: number }
  | { status: "stale"; data: T; cachedAt: number; userId: number } // устарело, но решением владельца (15.09) не удаляется — отдаётся с пометкой
  | { status: "empty" }; // записи нет — или хранилище недоступно (приватный режим, квота, запрет): ведёт себя так же

/**
 * Единый формат составного ключа для записей, зависящих от курса (и,
 * опционально, от дня внутри курса — контент дня). `kind` различает типы
 * данных в общем object store ("content_day", "progress_days", "badges").
 * Один источник формата на все модули-потребители — см. комментарий M5-STORE2 выше.
 */
export function compositeKey(kind: string, courseId: number | string, day?: number | string): string {
  return day === undefined ? `${kind}:${courseId}` : `${kind}:${courseId}:${day}`;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("indexedDB is not available in this environment"));
      return;
    }
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (e) {
      reject(e);
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(RECORDS_STORE)) {
        db.createObjectStore(RECORDS_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("indexedDB open failed"));
    req.onblocked = () => reject(new Error("indexedDB open blocked"));
  });
}

/** Записать значение под ключом. Недоступность хранилища не бросает исключение — только лог. */
export async function put<T>(key: string, data: T, userId: number): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(RECORDS_STORE, "readwrite");
      const record: StoreRecord<T> = { data, cachedAt: Date.now(), userId };
      tx.objectStore(RECORDS_STORE).put(record, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error("indexedDB put transaction failed"));
      tx.onabort = () => reject(tx.error ?? new Error("indexedDB put transaction aborted"));
    });
    db.close();
  } catch (e) {
    console.error("[store] put failed:", e instanceof Error ? e.message : String(e));
  }
}

/** Прочитать значение по ключу — трёхзначно: свежее / устаревшее (не удаляется, только помечается) / записи нет. */
export async function get<T>(key: string): Promise<StoreGetResult<T>> {
  try {
    const db = await openDb();
    const record = await new Promise<StoreRecord<T> | undefined>((resolve, reject) => {
      const tx = db.transaction(RECORDS_STORE, "readonly");
      const req = tx.objectStore(RECORDS_STORE).get(key);
      req.onsuccess = () => resolve(req.result as StoreRecord<T> | undefined);
      req.onerror = () => reject(req.error ?? new Error("indexedDB get request failed"));
    });
    db.close();

    if (!record) return { status: "empty" };

    const age = Date.now() - record.cachedAt;
    if (age > STALE_AFTER_MS) {
      return { status: "stale", data: record.data, cachedAt: record.cachedAt, userId: record.userId };
    }
    return { status: "fresh", data: record.data, cachedAt: record.cachedAt, userId: record.userId };
  } catch (e) {
    console.error("[store] get failed:", e instanceof Error ? e.message : String(e));
    return { status: "empty" };
  }
}

/** Полная очистка хранилища. Нужна при выходе и при смене пользователя на устройстве — существует с первого дня (веха П1). */
export async function clearAll(): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(RECORDS_STORE, "readwrite");
      tx.objectStore(RECORDS_STORE).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error("indexedDB clear transaction failed"));
      tx.onabort = () => reject(tx.error ?? new Error("indexedDB clear transaction aborted"));
    });
    db.close();
  } catch (e) {
    console.error("[store] clearAll failed:", e instanceof Error ? e.message : String(e));
  }
}
